import PersonAddIcon from "@mui/icons-material/PersonAdd";
import IconButton from "@mui/material/IconButton";
import { styled, Theme } from "@mui/material";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EntityMembersTable from "_shared/entity-members/_views/entity-members-table";
import Guid from "_shared/guids/guid";
import AdministrationPermissionsLoadState from "legal-entities/administrators/_views/administration-permissions-load-state";
import { PageTab } from "legal-entities/administrators/_views/administration-page-tabs";
import { deriveLegalEntityAdministrationAccess } from "legal-entities/administrators/administration-access";
import EntityMemberInvitationsTable from "legal-entities/invitations/member-invitations/_views/entity-member-invitations-table";
import EntityMemberInvitationForm from "legal-entities/invitations/member-invitations/_views/entity-member-invitation-form";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  type MRT_RowData as RowData,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  MRT_ShowHideColumnsButton as ToggleShowHideColumnsButton,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  TableTitleRow,
  ScrollableTabs,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { JSX, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";

const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
export default function OrganizationPage() {
  const routeParams = useLoaderData();
  const activeTab = routeParams?.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Users;

  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const administrationAccess = sessionPermissions
    ? deriveLegalEntityAdministrationAccess(sessionPermissions.permissions)
    : undefined;
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const isInviteUsersDialogOpen = useRef(false);
  const isViewProfileDialogOpen = useRef(false);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  const retrySessionPermissions = useCallback(() => {
    if (!sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissionsLoading]);

  const handleViewProfile = useCallback(
    (userId?: Guid): void => {
      if (!userId || isViewProfileDialogOpen.current) return;
      isViewProfileDialogOpen.current = true;
      const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: <ViewIndividualProfile userId={userId} />,
        onClose: () => {
          isViewProfileDialogOpen.current = false;
          navigate(parentPath.pathname);
        },
      });
    },
    [navigate, openDialog],
  );

  const handleInviteUsers = useCallback(() => {
    if (!administrationAccess?.canInviteMembers || isInviteUsersDialogOpen.current) return;
    isInviteUsersDialogOpen.current = true;
    const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);
    openDialog({
      title: "Invite member",
      resetStack: true,
      MuiProps: {
        maxWidth: "md",
      },
      component: <EntityMemberInvitationForm confirmDialog={confirm} />,
      onClose: () => {
        isInviteUsersDialogOpen.current = false;
        navigate(parentPath.pathname);
      },
    });
  }, [administrationAccess?.canInviteMembers, confirm, navigate, openDialog]);

  useEffect(() => {
    // Handle URL changes
    if (!administrationAccess) return;

    const firstAuthorizedTab = administrationAccess.canViewMembers
      ? PageTab.Users
      : administrationAccess.canViewInvitations
        ? PageTab.Invitations
        : undefined;
    const requestedTabIsAuthorized =
      (routeParams.pageTab === PageTab.Users && administrationAccess.canViewMembers) ||
      (routeParams.pageTab === PageTab.Invitations && administrationAccess.canViewInvitations);

    if ((!routeParams.pageTab || !requestedTabIsAuthorized) && firstAuthorizedTab) {
      navigate(`/organization/${firstAuthorizedTab}`);
    } else if (routeParams.action === "invite" && administrationAccess.canInviteMembers) {
      handleInviteUsers();
    } else if (routeParams.action === "view" && routeParams.id) {
      handleViewProfile(new Guid(routeParams.id));
    }
  }, [administrationAccess, handleInviteUsers, handleViewProfile, navigate, routeParams.pageTab, routeParams.action, routeParams.id]);

  const renderTopToolbarCustomActions = <T extends RowData>(
    props: { table: TableInstance<T> },
    additional?: JSX.Element | undefined,
    filters?: JSX.Element | undefined,
  ): JSX.Element => {
    const { table } = props;

    return (
      <TableTopToolbarContainer>
        <TableTitleRow>
          <TableTitle variant="h4">Organization</TableTitle>
          {isMediumDisplaySize && administrationAccess?.canInviteMembers && (
            <NewButton
              aria-label="Invite member"
              color="primary"
              onClick={() => navigate("/organization/invitations/invite")}
            >
              <PersonAddIcon fontSize="large" />
            </NewButton>
          )}
        </TableTitleRow>
        <LowerTopToolbarContainer>
          <ScrollableTabs
            allowScrollButtonsMobile
            value={routeParams.pageTab ?? PageTab.Users}
            indicatorColor="primary"
            scrollButtons="auto"
            textColor="primary"
            variant="scrollable"
            onChange={(_event, newValue) => navigate(`/organization/${newValue}`)}
          >
            {administrationAccess?.canViewMembers && <Tab value={PageTab.Users} label="Members" />}
            {administrationAccess?.canViewInvitations && (
              <Tab value={PageTab.Invitations} label="Pending invitations" />
            )}
          </ScrollableTabs>          {additional}
        </LowerTopToolbarContainer>
        {filters}
        {isMediumDisplaySize && (
          <MobileToolbarButtonsContainer>
            <GlobalFilterTextField table={table} />
            <ToolbarButtons>
              {table.options.enableColumnFilters !== false && <ToggleFiltersButton table={table} />}
              <ToggleShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </MobileToolbarButtonsContainer>
        )}
      </TableTopToolbarContainer>
    );
  };

  if (!sessionPermissions) {
    return <AdministrationPermissionsLoadState error={sessionPermissionsError} onRetry={retrySessionPermissions} />;
  }
  if (!administrationAccess || (!administrationAccess.canViewMembers && !administrationAccess.canViewInvitations)) {
    return <NotAuthorized />;
  }

  const entityMembersTable = (
    <EntityMembersTable
      canInviteMembers={administrationAccess.canInviteMembers}
      canManageMemberAccess={administrationAccess.canManageMemberAccess}
      renderTopToolbarCustomActions={renderTopToolbarCustomActions}
    />
  );

  switch (activeTab) {
    case PageTab.Users:
      return administrationAccess.canViewMembers
        ? entityMembersTable
        : <EntityMemberInvitationsTable
            canCancelInvitations={administrationAccess.canCancelMemberInvitations}
            canInviteMembers={administrationAccess.canInviteMembers}
            renderTopToolbarCustomActions={renderTopToolbarCustomActions}
          />;
    case PageTab.Invitations:
      return administrationAccess.canViewInvitations
        ? <EntityMemberInvitationsTable
            canCancelInvitations={administrationAccess.canCancelMemberInvitations}
            canInviteMembers={administrationAccess.canInviteMembers}
            renderTopToolbarCustomActions={renderTopToolbarCustomActions}
          />
        : entityMembersTable;
    default:
      return entityMembersTable;
  }
}
